import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { useUser, useFindOne, useSubscribe, useTracker } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Accounts } from '/imports/api/accounts';
import {
  ProjectManagementProjectInvoice, ProjectManagementProjectInvoices,
  projectManagementCanSeeFinances, projectManagementCanApproveInvoices, projectManagementInvoiceNumberLabel, projectManagementMoney as money,
} from '/imports/api/projectManagement';
import { ProjectManagementInvoiceReviewModal } from '/imports/ui/ProjectManagementInvoiceReviewModal';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';

const InvoicesPageStyled = styled(Page)`
  table { border-collapse: collapse; width: 100%; font-size: 0.9rem; }
  th, td { padding: 0.5rem 0.75rem; text-align: left; border-bottom: 1px solid ${css.grey7}; white-space: nowrap; }
  th { color: ${css.grey3}; font-weight: 600; font-size: 0.8rem; }
  th.num, td.num { text-align: right; }
  th { cursor: pointer; user-select: none; }
  th .sort-arrow { color: ${css.primaryColor}; margin-left: 0.25rem; }
  .muted { color: ${css.grey4}; }
  .empty { padding: 1.5rem 0; color: ${css.grey4}; }
  .controls { display: flex; gap: 1rem; align-items: center; margin-bottom: 0.75rem; flex-wrap: wrap; }
  .controls .status-select { min-width: 12rem; }
  .controls .search { min-width: 16rem; max-width: 28rem; flex: 1; }
`;

type SortKey = 'vendor' | 'invoiceNumber' | 'amount' | 'approvedAt' | 'approvedByName';

/**
 * The org's unpaid payables queue, across every project. Finance viewers only (super users, or accounting
 * + PM); super users can approve from here with the same review modal as the project Budget tab.
 * Filtering and sorting are client-side: the publication already sends the whole unpaid set, so there is
 * nothing to fetch. Unsorted means the order the rows arrive in, newest upload first.
 */
export const ProjectManagementInvoicesPage = () => {
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  const loading = useSubscribe(Publications.projectManagementInvoices)();
  const invoices = useTracker(
    () => ProjectManagementProjectInvoices.find({ active: { $ne: false }, paidAt: { $exists: false } }, { sort: { createdAt: -1 } }).fetch(),
    [],
  );
  const [reviewing, setReviewing] = useState<ProjectManagementProjectInvoice>();
  const [search, setSearch] = useState('');
  const [approval, setApproval] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (approval === 'approved' && !inv.approvedAt) return false;
      if (approval === 'awaiting' && inv.approvedAt) return false;
      if (!term) return true;
      // The number a finance viewer searches by is the one on screen, which for them is the Luminia number.
      return `${inv.vendor || ''} ${projectManagementInvoiceNumberLabel(inv, true)}`.toLowerCase().includes(term);
    });
  }, [invoices, search, approval]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    // Missing values collapse to '' / 0 so they group at one end rather than scattering; the queue's point
    // is to see the unapproved together.
    const value = (inv: ProjectManagementProjectInvoice) => {
      switch (sort.key) {
        case 'vendor': return (inv.vendor || '').toLowerCase();
        case 'invoiceNumber': return projectManagementInvoiceNumberLabel(inv, true).toLowerCase();
        case 'amount': return inv.amount ?? 0;
        case 'approvedAt': return inv.approvedAt ? new Date(inv.approvedAt).getTime() : 0;
        default: return (inv.approvedByName || '').toLowerCase();
      }
    };
    return [...filtered].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av < bv) return -sort.dir;
      if (av > bv) return sort.dir;
      return 0;
    });
  }, [filtered, sort]);

  if (!projectManagementCanSeeFinances(user, account)) return <NotPermittedPage />;
  const canApprove = projectManagementCanApproveInvoices(user, account);
  const toggleSort = (key: SortKey) => setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  const th = (key: SortKey, label: string, num?: boolean) => (
    <th className={num ? 'num' : undefined} onClick={() => toggleSort(key)}>
      {label}
      {sort?.key === key && <span className="sort-arrow">{sort.dir === 1 ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <InvoicesPageStyled className={`page${loading ? ' initializing' : ''}`}>
      <PageHeading left={<h1>{strings.Invoices()}</h1>} />
      <PageBody>
        <div className="controls">
          <span>{strings.Status()}:</span>
          <div className="status-select">
            <Select
              value={approval}
              options={[
                { value: '', label: strings.All() },
                { value: 'awaiting', label: strings.AwaitingApproval() },
                { value: 'approved', label: strings.Approved() },
              ]}
              onChange={(v) => setApproval(v || '')}
            />
          </div>
          <div className="search"><Input value={search} onChange={(v) => setSearch(v || '')} placeholder={strings.Search()} /></div>
        </div>
        {
          !loading && !invoices.length
            ? <div className="empty">{strings.NoUnpaidInvoices()}</div>
            : !loading && !sorted.length
              ? <div className="empty">{strings.NoMatchingInvoices()}</div>
              : <table>
                <thead>
                  <tr>
                    {th('vendor', strings.Vendor())}
                    {th('invoiceNumber', strings.InvoiceNumber())}
                    {th('amount', strings.Amount(), true)}
                    {th('approvedAt', strings.DateApproved())}
                    {th('approvedByName', strings.ApprovedBy())}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((inv) => (
                    <tr key={inv._id}>
                      <td>{inv.vendor || <span className="muted">—</span>}</td>
                      <td>{projectManagementInvoiceNumberLabel(inv, true)}</td>
                      <td className="num">{inv.amount != null ? money(inv.amount) : <span className="muted">—</span>}</td>
                      {/* Unapproved: invoice approvers get the Approve action; other finance viewers just see the gap. */}
                      <td>{
                        inv.approvedAt ? new Date(inv.approvedAt).toLocaleDateString()
                          : canApprove ? <button className="link-btn" onClick={() => setReviewing(inv)}>{strings.Approve()}</button>
                            : <span className="muted">—</span>
                      }</td>
                      <td>{inv.approvedByName || <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
        }
      </PageBody>
      {reviewing && <ProjectManagementInvoiceReviewModal invoice={reviewing} onClose={() => setReviewing(undefined)} />}
    </InvoicesPageStyled>
  );
};
